import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { PulseBars, Ring } from '../components/charts.tsx';
import { Icon } from '../components/Icon.tsx';
import { CountUp, Reveal } from '../components/motion.tsx';
import { Avatar, Button, Chip, Panel, Skeleton } from '../components/ui.tsx';
import { get, photoUrl } from '../lib/api.ts';
import { date, greeting, idr, time } from '../lib/format.ts';
import { useMe, type Me } from '../lib/session.ts';

interface DashData {
  today: string;
  headcount: number;
  today_: { scheduled: number; present: number; late: number; missing: number; onLeave: number };
  pending: { leave: number; overtime: number; corrections: number; profile: number; canApprove: boolean };
  birthdays: { id: string; name: string; birthDate: string }[];
  contracts: { id: string; name: string; contractEndsOn: string }[];
  trend: { date: string; present: number; late: number; scheduled: number }[];
}

function useNow(ms = 15_000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export function Dashboard() {
  const me = useMe().data!;
  return me.access.people ? <Overview me={me} /> : <MyDay me={me} />;
}

function Overview({ me }: { me: Me }) {
  const q = useQuery({ queryKey: ['dashboard'], queryFn: () => get<DashData>('/api/dashboard'), refetchInterval: 60_000 });
  const tz = me.employee?.timeZone ?? 'Asia/Makassar';
  const now = useNow();
  const d = q.data;
  const first = (me.employee?.givenName ?? me.displayName).split(' ')[0];
  const waiting = d ? d.pending.leave + d.pending.overtime + d.pending.corrections + d.pending.profile : 0;

  return (
    <Reveal className="stack" >
      <Reveal.Item as="header" className="page-head" style={{ display: 'block' }}>
        <div className="grid-field" />
        <div className="dash-hero">
          <div className="stack" style={{ ['--gap' as string]: '18px' }}>
            <span className="eyebrow">
              {date(d?.today ?? new Date().toISOString(), { weekday: true })} · {me.employee?.officeName ?? 'All offices'}
            </span>
            <h1 className="page-head__title">
              {greeting(tz)},<br />
              <em>{first}.</em>
            </h1>
            {d ? (
              <p className="page-head__lede">
                {d.today_.present} of {d.today_.scheduled} people are in so far. {d.today_.late ? `${d.today_.late} arrived late` : 'Nobody arrived late'}
                {d.pending.canApprove ? (waiting ? `, and ${waiting} request${waiting > 1 ? 's are' : ' is'} waiting on you.` : ', and your inbox is clear.') : '.'}
              </p>
            ) : (
              <Skeleton h={22} w="60%" />
            )}
          </div>
          <div className="dash-now">
            <span className="eyebrow">Local time · {tz === 'Asia/Jakarta' ? 'WIB' : 'WITA'}</span>
            <span className="dash-now__time num">{time(now, tz)}</span>
            <div className="row" style={{ ['--gap' as string]: '8px' }}>
              <Link to="/attendance">
                <Button size="sm" icon="clock">
                  Live board
                </Button>
              </Link>
              {d?.pending.canApprove ? (
                <Link to="/approvals">
                  <Button size="sm" variant="primary" icon="inbox">
                    Review {waiting || ''}
                  </Button>
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </Reveal.Item>

      <Reveal.Item>
        <div className="ledger" style={{ ['--cols' as string]: 4 }}>
          <div className="ledger__cell">
            <div className="ledger__value">{d ? <CountUp value={d.headcount} /> : '—'}</div>
            <div className="ledger__label eyebrow">Active people</div>
            <div className="ledger__delta">across your scope</div>
          </div>
          <div className="ledger__cell">
            <div className="ledger__value">
              {d ? <CountUp value={d.today_.present} /> : '—'}
              <small>/ {d?.today_.scheduled ?? '—'}</small>
            </div>
            <div className="ledger__label eyebrow">In today</div>
            <div className="ledger__delta">{d ? `${d.today_.onLeave} on leave` : ''}</div>
          </div>
          <div className="ledger__cell">
            <div className="ledger__value" style={{ color: d && d.today_.late ? 'var(--clay)' : undefined }}>
              {d ? <CountUp value={d.today_.late} /> : '—'}
            </div>
            <div className="ledger__label eyebrow">Late arrivals</div>
            <div className="ledger__delta">{d ? `${d.today_.missing} not yet in` : ''}</div>
          </div>
          <div className="ledger__cell ledger__cell--accent">
            <div className="ledger__value">{d ? <CountUp value={waiting} /> : '—'}</div>
            <div className="ledger__label eyebrow">Waiting on you</div>
            <div className="ledger__delta">{d ? `${d.pending.leave} leave · ${d.pending.overtime} overtime · ${d.pending.corrections} corrections` : ''}</div>
          </div>
        </div>
      </Reveal.Item>

      <Reveal.Item className="dash-split" style={{ marginTop: 'var(--s-6)' }}>
        <Panel eyebrow="Last 14 days" title="Attendance pulse" action={<Link to="/attendance" className="link faint" style={{ fontSize: 'var(--step--1)' }}>Open board</Link>}>
          {d ? <PulseBars data={d.trend} /> : <Skeleton h={168} />}
        </Panel>
        <div className="stack">
          <Panel eyebrow="Next two weeks" title="Birthdays">
            {d?.birthdays.length ? (
              <ul className="list-plain">
                {d.birthdays.map((b) => (
                  <li key={b.id} className="mini-row">
                    <Avatar name={b.name} size={28} photo={photoUrl(b.id)} />
                    <Link to="/people/$id" params={{ id: b.id }} className="link">
                      {b.name}
                    </Link>
                    <span className="mini-row__aside">{date(b.birthDate, { year: false })}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="faint">No birthdays coming up.</p>
            )}
          </Panel>
        </div>
      </Reveal.Item>

      <Reveal.Item className="grid-12" style={{ marginTop: 'var(--s-5)' }}>
        <div className="col-5">
          <Panel eyebrow="Within 45 days" title={<>Contracts <em className="serif-italic faint">ending</em></>}>
            {d?.contracts.length ? (
              <ul className="list-plain">
                {d.contracts.map((c) => {
                  const days = Math.round((Date.parse(c.contractEndsOn) - Date.parse(d.today)) / 86_400_000);
                  return (
                    <li key={c.id} className="mini-row">
                      <Link to="/people/$id" params={{ id: c.id }} className="link">
                        {c.name}
                      </Link>
                      <span style={{ marginLeft: 'auto' }}>
                        <Chip tone={days <= 14 ? 'bad' : 'wait'}>{days}d</Chip>
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="faint">No contracts end soon.</p>
            )}
          </Panel>
        </div>
        <div className="col-7">
          <Panel eyebrow="Quick actions" title="Get things done">
            <div className="grid-12" style={{ gap: 12 }}>
              {[
                { to: '/approvals', icon: 'inbox' as const, label: 'Review requests', hint: 'Leave, overtime and corrections', show: me.access.approvals },
                { to: '/payroll', icon: 'wallet' as const, label: 'Run payroll', hint: 'Calculate, review and approve', show: me.access.payroll },
                { to: '/roster', icon: 'roster' as const, label: 'Plan the roster', hint: 'Assign shifts and days off', show: me.access.attendance },
                { to: '/people', icon: 'people' as const, label: 'Find someone', hint: 'Directory and profiles', show: me.access.people },
              ]
                .filter((a) => a.show)
                .map((a) => (
                  <Link key={a.to} to={a.to} className="col-6 panel panel--interactive" style={{ padding: 18, display: 'grid', gap: 10 }}>
                    <Icon name={a.icon} width={20} height={20} style={{ color: 'var(--accent-text)' }} />
                    <span style={{ fontWeight: 600 }}>{a.label}</span>
                    <span className="faint" style={{ fontSize: 'var(--step--2)' }}>
                      {a.hint}
                    </span>
                  </Link>
                ))}
            </div>
          </Panel>
        </div>
      </Reveal.Item>
    </Reveal>
  );
}

interface CheckinStatus {
  today: string;
  timeZone: string;
  timesheet: { clockInAt: string | null; clockOutAt: string | null; lateMinutes: number; status: string; shiftStartAt: string | null; shiftEndAt: string | null } | null;
}

function MyDay({ me }: { me: Me }) {
  const tz = me.employee?.timeZone ?? 'Asia/Makassar';
  const now = useNow();
  const status = useQuery({ queryKey: ['checkin-status'], queryFn: () => get<CheckinStatus>('/api/checkin/status'), enabled: !!me.employee });
  const balance = useQuery({ queryKey: ['leave-balance'], queryFn: () => get<{ remaining: number; entitlement: number; used: number; pending: number }>('/api/requests/leave/balance'), enabled: !!me.employee });
  const slips = useQuery({ queryKey: ['my-payslips'], queryFn: () => get<{ id: string; netPay: string; periodFrom: string; periodTo: string }[]>('/api/payroll/me/payslips'), enabled: !!me.employee });
  const ts = status.data?.timesheet;
  const first = (me.employee?.givenName ?? me.displayName).split(' ')[0];
  return (
    <Reveal className="stack">
      <Reveal.Item as="header" className="page-head" style={{ display: 'block' }}>
        <div className="grid-field" />
        <span className="eyebrow">{date(status.data?.today ?? new Date().toISOString(), { weekday: true })}</span>
        <h1 className="page-head__title" style={{ marginTop: 18 }}>
          {greeting(tz)}, <em>{first}.</em>
        </h1>
      </Reveal.Item>
      <Reveal.Item className="today-card">
        <div>
          <span className="eyebrow">Today’s shift</span>
          <div className="display" style={{ fontSize: 'var(--step-3)', marginTop: 8 }}>
            {ts?.shiftStartAt ? `${time(ts.shiftStartAt, tz)} – ${time(ts.shiftEndAt, tz)}` : 'No shift scheduled'}
          </div>
          <div className="today-card__times">
            <div>
              <div className="eyebrow">In</div>
              <div className="display num">{ts?.clockInAt ? time(ts.clockInAt, tz) : '—'}</div>
            </div>
            <div>
              <div className="eyebrow">Out</div>
              <div className="display num">{ts?.clockOutAt ? time(ts.clockOutAt, tz) : '—'}</div>
            </div>
            {ts?.lateMinutes ? (
              <div>
                <div className="eyebrow">Late</div>
                <div className="display num" style={{ color: 'var(--clay)' }}>
                  {ts.lateMinutes}m
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <div className="stack" style={{ justifyItems: 'end' }}>
          <span className="big-clock num">{time(now, tz)}</span>
          <Link to="/check-in">
            <Button variant="primary" size="lg" icon="face">
              {ts?.clockInAt && !ts.clockOutAt ? 'Check out' : 'Check in'}
            </Button>
          </Link>
        </div>
      </Reveal.Item>
      <Reveal.Item className="grid-12" style={{ marginTop: 'var(--s-5)' }}>
        <div className="col-4">
          <Panel eyebrow="Annual leave" title="Balance">
            <div className="row" style={{ ['--gap' as string]: '22px' }}>
              {balance.data ? <Ring value={balance.data.remaining} max={balance.data.entitlement} label="Annual leave remaining" sublabel="days left" /> : <Skeleton w={132} h={132} r={66} />}
              <div className="stack" style={{ ['--gap' as string]: '8px' }}>
                <span className="muted">{balance.data?.used ?? 0} used</span>
                <span className="muted">{balance.data?.pending ?? 0} pending</span>
                <Link to="/time" className="link accent-text">
                  Request leave
                </Link>
              </div>
            </div>
          </Panel>
        </div>
        <div className="col-8">
          <Panel eyebrow="Pay" title="Latest payslips" action={<Link to="/payslips" className="link faint">All payslips</Link>}>
            {slips.data?.length ? (
              <ul className="list-plain">
                {slips.data.slice(0, 4).map((s) => (
                  <li key={s.id} className="mini-row">
                    <Icon name="file" width={16} height={16} className="faint" />
                    <Link to="/payslips/$id" params={{ id: s.id }} className="link">
                      {date(s.periodFrom, { year: false })} – {date(s.periodTo)}
                    </Link>
                    <span className="mini-row__aside num" style={{ fontSize: 13, color: 'var(--ink-1)' }}>
                      {idr(s.netPay)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="faint">Your payslips will appear here once a payroll run is approved.</p>
            )}
          </Panel>
        </div>
      </Reveal.Item>
    </Reveal>
  );
}
